import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './context/AuthContext';
import LoadingScreen from './components/LoadingScreen';

// Layout
import Navbar from './components/Layout/Navbar';
import NavbarConditional from './components/Layout/NavbarConditional';
import Footer from './components/Layout/Footer';
import CookieConsent from './components/CookieConsent';

// Pages
import Home from './pages/Home';
import FAQ from './pages/FAQ';
import Aide from './pages/Aide';
import Accessibilite from './pages/Accessibilite';
import PolitiqueConfidentialite from './pages/PolitiqueConfidentialite';
import SupprimerCompte from './pages/SupprimerCompte';
import CGU from './pages/CGU';
import MentionsLegales from './pages/MentionsLegales';
import NotFound from './pages/NotFound';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import ForgotPassword from './pages/Auth/ForgotPassword';
import Dashboard from './pages/Dashboard';

// Restaurants
import RestaurantHome from './pages/Restaurant/RestaurantHome';
import RestaurantList from './pages/Restaurant/RestaurantList';
import RestaurantDetail from './pages/Restaurant/RestaurantDetail';
import RestaurantOrders from './pages/Restaurant/RestaurantOrders';
import OrderTracking from './pages/Restaurant/OrderTracking';

// VTC
import VTCRequest from './pages/VTC/VTCRequest';
import VTCRides from './pages/VTC/VTCRides';

// Logistics
import LogisticsCreate from './pages/Logistics/LogisticsCreate';
import LogisticsOrders from './pages/Logistics/LogisticsOrders';
import LogisticsTracking from './pages/Logistics/LogisticsTracking';

// Courses
import CoursesList from './pages/Courses/CoursesList';
import CoursesOrders from './pages/Courses/CoursesOrders';

// Covoiturage
import CarpoolSearch from './pages/Carpool/CarpoolSearch';
import CarpoolCreate from './pages/Carpool/CarpoolCreate';
import CarpoolBookings from './pages/Carpool/CarpoolBookings';

// Car Rental
import CarRentalSearch from './pages/CarRental/CarRentalSearch';
import CarRentalDetail from './pages/CarRental/CarRentalDetail';
import CarRentalBookings from './pages/CarRental/CarRentalBookings';
import RentMyVehicle from './pages/CarRental/RentMyVehicle';

// Profile
import Profile from './pages/Profile/Profile';
import Addresses from './pages/Profile/Addresses';
import PaymentMethods from './pages/Profile/PaymentMethods';

// Delivery
import DeliveryDashboard from './pages/Delivery/DeliveryDashboard';
import ActiveDelivery from './pages/Delivery/ActiveDelivery';
import DeliveryHistory from './pages/Delivery/DeliveryHistory';

// Admin
import AdminDashboard from './pages/Admin/AdminDashboard';
import PartnerVerification from './pages/Admin/PartnerVerification';

// Partner
import PartnerDashboard from './pages/Partner/PartnerDashboard';
import RestaurantPartner from './pages/Partner/RestaurantPartner';
import VTCPartner from './pages/Partner/VTCPartner';
import StorePartner from './pages/Partner/StorePartner';
import CarRentalPartner from './pages/Partner/CarRentalPartner';
import CarpoolPartner from './pages/Partner/CarpoolPartner';
import DeliveryPartner from './pages/Partner/DeliveryPartner';
import PartnerRegister from './pages/Partner/PartnerRegister';
import PartnerPayouts from './pages/Partner/PartnerPayouts';
import VerificationPending from './pages/Partner/VerificationPending';
import StripeOnboarding from './pages/Partner/StripeOnboarding';
import RestaurantDashboard from './pages/Partner/RestaurantDashboard';

// Protected Route
import ProtectedRoute from './components/Auth/ProtectedRoute';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#552583',
      light: '#7a3aad',
      dark: '#33154f',
    },
    secondary: {
      main: '#FDB927',
      light: '#ffd060',
      dark: '#c98a00',
    },
    background: {
      default: '#E4F0F6',
      paper: 'rgba(230, 221, 212, 0.85)',
    },
    text: {
      primary: '#1a1a1a',
      secondary: '#4a4a4a',
      disabled: '#7a7a7a',
    },
    success: {
      main: '#F0EBE0',
    },
    error: {
      main: '#e74c3c',
    },
  },
  typography: {
    fontFamily: '"Montserrat", "Poppins", sans-serif',
    allVariants: {
      color: '#1a1a1a',
    },
    h1: {
      fontWeight: 800,
      fontSize: 'clamp(1.8rem, 6vw, 4rem)',
      letterSpacing: '0.02em',
      textTransform: 'uppercase',
      color: '#111111',
    },
    h2: {
      fontWeight: 800,
      fontSize: 'clamp(1.5rem, 5vw, 3rem)',
      letterSpacing: '0.02em',
      textTransform: 'uppercase',
      color: '#111111',
    },
    h3: {
      fontWeight: 700,
      fontSize: 'clamp(1.2rem, 4vw, 2rem)',
      letterSpacing: '0.01em',
      color: '#1a1a1a',
    },
    h4: {
      fontWeight: 700,
      fontSize: 'clamp(1.1rem, 3vw, 1.5rem)',
      color: '#1a1a1a',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      color: '#1a1a1a',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      color: '#1a1a1a',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.7,
      color: '#2a2a2a',
    },
    body2: {
      color: '#3a3a3a',
    },
    caption: {
      color: '#4a4a4a',
    },
    subtitle1: {
      color: '#2a2a2a',
    },
    subtitle2: {
      color: '#3a3a3a',
    },
    button: {
      textTransform: 'uppercase',
      fontWeight: 700,
      letterSpacing: '0.1em',
    },
  },
  shape: {
    borderRadius: 0,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        'html, body': { overflowX: 'hidden' },
        '#root': { maxWidth: '100%' },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          padding: '14px 32px',
          fontSize: '0.875rem',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.25s ease-out',
        },
        contained: {
          background: '#552583',
          color: '#ffffff',
          boxShadow: '0 1px 3px rgba(85,37,131,0.15), 0 6px 24px -4px rgba(85,37,131,0.25)',
          border: '0.5px solid rgba(253,185,39,0.2)',
          '&:hover': {
            background: '#3d1a61',
            boxShadow: '0 1px 3px rgba(85,37,131,0.2), 0 10px 32px -4px rgba(85,37,131,0.3)',
            transform: 'translateY(-1px)',
          },
        },
        outlined: {
          background: 'transparent',
          border: '1.5px solid rgba(85,37,131,0.25)',
          '&:hover': {
            background: 'rgba(85,37,131,0.04)',
            border: '1.5px solid rgba(85,37,131,0.5)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          background: 'rgba(255,255,255,0.65)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '0.5px solid rgba(85,37,131,0.08)',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 8px 32px -8px rgba(85,37,131,0.08)',
          transition: 'all 0.3s ease-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 16px 48px -12px rgba(85,37,131,0.14)',
            border: '0.5px solid rgba(85,37,131,0.15)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          background: 'rgba(255,255,255,0.6)',
          backdropFilter: 'blur(20px)',
          border: '0.5px solid rgba(85,37,131,0.08)',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 6px 24px -6px rgba(85,37,131,0.06)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: '#552583',
          backdropFilter: 'none',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            background: 'rgba(85,37,131,0.03)',
            border: '1px solid rgba(85,37,131,0.1)',
            borderRadius: '10px',
            transition: 'all 0.2s ease-out',
            '& fieldset': {
              border: 'none',
            },
            '&:hover': {
              background: 'rgba(85,37,131,0.06)',
              border: '1px solid rgba(85,37,131,0.2)',
            },
            '&.Mui-focused': {
              background: 'rgba(85,37,131,0.08)',
              border: '1px solid rgba(85,37,131,0.4)',
              boxShadow: '0 0 0 3px rgba(85,37,131,0.06)',
            },
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        MenuProps: {
          disableScrollLock: true,
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
          PaperProps: { sx: { maxHeight: 280 } },
        },
      },
    },
    MuiMenu: {
      defaultProps: {
        disableScrollLock: true,
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        transformOrigin: { vertical: 'top', horizontal: 'left' },
      },
    },
    MuiModal: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
  },
});

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function FooterConditional() {
  const { pathname } = useLocation();
  if (pathname === '/restaurants') return null;
  if (pathname.startsWith('/dashboard') || pathname.startsWith('/partner/') || pathname.startsWith('/delivery/dashboard') || pathname.startsWith('/admin')) return null;
  return <Footer />;
}

function App() {
  // Skip loading screen on callback routes (Stripe return, password reset, etc.)
  const skipLoading = /\/(partner\/stripe|reset-password)/.test(window.location.pathname);
  const [loading, setLoading] = useState(!skipLoading);

  useEffect(() => {
    if (!loading) return;
    const timer = setTimeout(() => setLoading(false), 1200);
    return () => clearTimeout(timer);
  }, [loading]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {loading && <LoadingScreen onFinish={() => setLoading(false)} />}
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <NavbarConditional />
            <main style={{ flex: 1 }}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Home />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/aide" element={<Aide />} />
                <Route path="/accessibilite" element={<Accessibilite />} />
                <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
                <Route path="/supprimer-compte" element={<SupprimerCompte />} />
                <Route path="/cgu" element={<CGU />} />
                <Route path="/mentions-legales" element={<MentionsLegales />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ForgotPassword />} />

                {/* Protected Routes */}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />

                {/* Restaurant Routes */}
                <Route path="/restaurants" element={<RestaurantList />} />
                <Route path="/restaurants/list" element={<RestaurantList />} />
                <Route path="/restaurants/:slug" element={<RestaurantDetail />} />
                <Route path="/restaurant/orders" element={
                  <ProtectedRoute>
                    <RestaurantOrders />
                  </ProtectedRoute>
                } />
                <Route path="/restaurant/orders/:id/tracking" element={
                  <ProtectedRoute>
                    <OrderTracking />
                  </ProtectedRoute>
                } />

                {/* VTC Routes */}
                <Route path="/vtc" element={
                  <ProtectedRoute>
                    <VTCRequest />
                  </ProtectedRoute>
                } />
                <Route path="/vtc/rides" element={
                  <ProtectedRoute>
                    <VTCRides />
                  </ProtectedRoute>
                } />

                {/* Logistics Routes */}
                <Route path="/logistics" element={
                  <ProtectedRoute>
                    <LogisticsCreate />
                  </ProtectedRoute>
                } />
                <Route path="/logistics/orders" element={
                  <ProtectedRoute>
                    <LogisticsOrders />
                  </ProtectedRoute>
                } />
                <Route path="/logistics/track/:trackingNumber" element={
                  <LogisticsTracking />
                } />

                {/* Courses Routes */}
                <Route path="/courses" element={
                  <ProtectedRoute>
                    <CoursesList />
                  </ProtectedRoute>
                } />
                <Route path="/courses/orders" element={
                  <ProtectedRoute>
                    <CoursesOrders />
                  </ProtectedRoute>
                } />

                {/* Carpool Routes */}
                <Route path="/carpool" element={<CarpoolSearch />} />
                <Route path="/carpool/create" element={
                  <ProtectedRoute>
                    <CarpoolCreate />
                  </ProtectedRoute>
                } />
                <Route path="/carpool/bookings" element={
                  <ProtectedRoute>
                    <CarpoolBookings />
                  </ProtectedRoute>
                } />

                {/* Car Rental Routes */}
                <Route path="/car-rental" element={<CarRentalSearch />} />
                <Route path="/car-rental/:id" element={<CarRentalDetail />} />
                <Route path="/car-rental/bookings" element={
                  <ProtectedRoute>
                    <CarRentalBookings />
                  </ProtectedRoute>
                } />
                <Route path="/car-rental/rent-my-vehicle" element={
                  <ProtectedRoute>
                    <RentMyVehicle />
                  </ProtectedRoute>
                } />

                {/* Profile Routes */}
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                } />
                <Route path="/profile/addresses" element={
                  <ProtectedRoute>
                    <Addresses />
                  </ProtectedRoute>
                } />
                <Route path="/profile/payment-methods" element={
                  <ProtectedRoute>
                    <PaymentMethods />
                  </ProtectedRoute>
                } />

                {/* Delivery Routes */}
                <Route path="/delivery/dashboard" element={
                  <ProtectedRoute>
                    <DeliveryDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/delivery/order/:id" element={
                  <ProtectedRoute>
                    <ActiveDelivery />
                  </ProtectedRoute>
                } />
                <Route path="/delivery/active" element={
                  <ProtectedRoute>
                    <ActiveDelivery />
                  </ProtectedRoute>
                } />
                <Route path="/delivery/history" element={
                  <ProtectedRoute>
                    <DeliveryHistory />
                  </ProtectedRoute>
                } />

                {/* Admin Routes */}
                <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="/admin/dashboard" element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/admin/partner-verification" element={
                  <ProtectedRoute requiredRole="admin">
                    <PartnerVerification />
                  </ProtectedRoute>
                } />

                {/* Partner Routes */}
                <Route path="/livreur" element={<Navigate to="/partner/register?type=livreur" replace />} />
                <Route path="/partner/register" element={<PartnerRegister />} />
                <Route path="/partner/verification-pending" element={<VerificationPending />} />
                <Route path="/partner/dashboard" element={
                  <ProtectedRoute>
                    <PartnerDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/partner/restaurant" element={
                  <ProtectedRoute>
                    <RestaurantPartner />
                  </ProtectedRoute>
                } />
                <Route path="/partner/restaurant/dashboard" element={
                  <ProtectedRoute>
                    <RestaurantDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/partner/vtc" element={
                  <ProtectedRoute>
                    <VTCPartner />
                  </ProtectedRoute>
                } />
                <Route path="/partner/store" element={
                  <ProtectedRoute>
                    <StorePartner />
                  </ProtectedRoute>
                } />
                <Route path="/partner/car-rental" element={
                  <ProtectedRoute>
                    <CarRentalPartner />
                  </ProtectedRoute>
                } />
                <Route path="/partner/carpool" element={
                  <ProtectedRoute>
                    <CarpoolPartner />
                  </ProtectedRoute>
                } />
                <Route path="/partner/delivery" element={
                  <ProtectedRoute>
                    <DeliveryPartner />
                  </ProtectedRoute>
                } />
                <Route path="/partner/payouts" element={
                  <ProtectedRoute>
                    <PartnerPayouts />
                  </ProtectedRoute>
                } />
                <Route path="/partner/stripe/onboard" element={
                  <ProtectedRoute>
                    <StripeOnboarding />
                  </ProtectedRoute>
                } />
                <Route path="/partner/stripe/return" element={
                  <ProtectedRoute>
                    <StripeOnboarding />
                  </ProtectedRoute>
                } />

                {/* 404 */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
            <FooterConditional />
            <CookieConsent />
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
